import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import {
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Row,
  Col,
  Statistic,
  Progress
} from 'antd';
import {
  RocketOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  BankOutlined
} from '@ant-design/icons';
import { ThemeContext } from '../../context/ThemeContext';
import { useContext } from 'react';
import './AdminPages.css';

const { Title, Paragraph } = Typography;


const GovernmentNSDL = () => {
  const { isDark } = useContext(ThemeContext);
  const [loadingStates, setLoadingStates] = useState({});
  const [data, setData] = useState([]);
  const [botExecutions, setBotExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [runAllLoading, setRunAllLoading] = useState(false);

  // Bot configuration with dynamic API endpoints
  const botConfig = {
    'rbi-g-sec': {
      name: 'RBI Government Securities',
      endpoint: '/trigger/data-sources/rbi',
      status: 'Active',
      successRate: 97,
      lastRun: '1 hour ago',
      totalRuns: 400,
      successfulRuns: 388
    },
    'nsdl-inventory': {
      name: 'NSDL Inventory',
      endpoint: '/trigger/data-sources/nsdl',
      status: 'Active',
      successRate: 97,
      lastRun: '1 hour ago',
      totalRuns: 400,
      successfulRuns: 388
    },
    'bse': {
      name: 'BSE',
      endpoint: '/trigger/data-sources/bse',
      status: 'Active',
      successRate: 95,
      lastRun: '2 hours ago',
      totalRuns: 300,
      successfulRuns: 285
    },
    'ccil-trade': {
      name: 'CCIL Inventory',
      endpoint: '/trigger/data-sources/ccil',
      status: 'Active',
      successRate: 99,
      lastRun: '30 minutes ago',
      totalRuns: 500,
      successfulRuns: 495
    },
    'nse': {
      name: 'NSE',
      endpoint: '/trigger/data-sources/nse',
      status: 'Paused',
      successRate: 90,
      lastRun: '3 hours ago',
      totalRuns: 200,
      successfulRuns: 180
    },
    // 'nsdl-preference': {
    //   name: 'NSDL Preference Shares',
    //   endpoint: '/trigger/data-sources/nsdl/preference',
    //   status: 'Failed',
    //   successRate: 0,
    //   lastRun: '4 hours ago',
    //   totalRuns: 5,
    //   successfulRuns: 0
    // },
    // 'nsdl-securitised': {
    //   name: 'NSDL Securitised Instruments',
    //   endpoint: '/trigger/data-sources/nsdl/securitised',
    //   status: 'Active',
    //   successRate: 98,
    //   lastRun: '45 minutes ago',
    //   totalRuns: 150,
    //   successfulRuns: 147
    // }
  };

  // Convert bot config to array format for table
  const mockData = Object.entries(botConfig).map(([key, config]) => ({
    key,
    ...config
  }));

  // Fetch bot executions from API
  const fetchBotExecutions = async () => {
    setLoadingExecutions(true);
    try {
      const response = await apiClient.get('http://127.0.0.1:8082/api/v1/migration/bot-executions');
      setBotExecutions(response.data);
    } catch (error) {
      console.error('Error fetching bot executions:', error);
      message.error('Failed to fetch bot execution data');
    } finally {
      setLoadingExecutions(false);
    }
  };

  // Map bot executions to bot names
  const getBotExecutionData = (botName) => {
    // Create a mapping between static bot names and API bot names
    const nameMapping = {
      'RBI Government Securities': ['RBI-G-Sec', 'RBI'],
      'NSDL Inventory': ['NSDL-Inventories', 'NSDL'],
      'BSE': ['BSE-Defaulted-Debt', 'BSE'],
      'CCIL Inventory': ['CCIL-Trade-Data', 'CCIL'],
      'NSE': ['NSE-Trade-Repositories', 'NSE']
    };

    const possibleNames = nameMapping[botName] || [botName];
    
    const execution = botExecutions.find(exec => 
      possibleNames.some(name => 
        exec.botName === name || 
        exec.botName.includes(name) ||
        name.includes(exec.botName)
      )
    );
    return execution;
  };


  // Update data with API status and real execution data
  const updateDataWithAPIStatus = () => {
    const updatedData = mockData.map(item => {
      const executionData = getBotExecutionData(item.name);
      
      return {
        ...item,
        status: executionData ? executionData.status : item.status,
        // Update with real data from API if available
        lastRun: executionData ? new Date(executionData.executionTime).toLocaleString() : item.lastRun,
        totalRuns: executionData ? (item.totalRuns + 1) : item.totalRuns,
        successfulRuns: executionData && executionData.status === 'SUCCESS' ? (item.successfulRuns + 1) : item.successfulRuns,
        successRate: executionData ? Math.round(((executionData.status === 'SUCCESS' ? item.successfulRuns + 1 : item.successfulRuns) / (item.totalRuns + 1)) * 100) : item.successRate
      };
    });
    setData(updatedData);
  };

  useEffect(() => {
    // Initialize with botConfig data immediately
    setData(mockData);
    fetchBotExecutions();
  }, []);

  // Update data when bot executions are loaded
  useEffect(() => {
    if (botExecutions.length > 0) {
      updateDataWithAPIStatus();
    } else {
      // If no API data, show botConfig data
      setData(mockData);
    }
  }, [botExecutions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'RUNNING':
        return 'processing';
      case 'Active':
        return 'success';
      case 'Failed':
        return 'error';
      case 'Paused':
        return 'warning';
      case 'Disabled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircleOutlined />;
      case 'FAILED':
        return <CloseCircleOutlined />;
      case 'RUNNING':
        return <PlayCircleOutlined />;
      case 'Active':
        return <CheckCircleOutlined />;
      case 'Failed':
        return <CloseCircleOutlined />;
      case 'Paused':
        return <PauseCircleOutlined />;
      case 'Disabled':
        return <MinusCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const handleRunAll = async () => {
    // Set loading state for Run All button
    setRunAllLoading(true);
    
    // Set loading state for all individual bot buttons
    setLoadingStates(prev => {
      const newStates = {};
      mockData.forEach(item => {
        newStates[item.key] = true;
      });
      return newStates;
    });
    
    message.loading('Running all government & NSDL bots...', 0);
    
    try {
      // Execute all bots in parallel
      const promises = mockData.map(item => {
        const config = botConfig[item.key];
        if (config) {
          return executeBot(item.key, config);
        }
        return Promise.resolve();
      });
      
      await Promise.allSettled(promises);
      
      message.destroy();
      message.success('All government & NSDL bots execution completed!');
    } catch (error) {
      message.destroy();
      message.error('Some bots failed to execute. Check individual bot status.');
    } finally {
      // Clear loading state for all buttons
      setLoadingStates({});
      setRunAllLoading(false);
    }
  };

  // Dynamic bot execution function
  const executeBot = async (botKey, config) => {
    const baseUrl = process.env.REACT_APP_BOT_URL;
    const fullUrl = `${baseUrl}${config.endpoint}`;
    
    try {
      // Execute GET request
      const response = await apiClient.get(fullUrl);
      
      console.log('API Response:', response.data);
      
      // Handle the API response structure
      if (response.data.status === 'success') {
        message.destroy(); // Remove loading message
        message.success(response.data.message || `${config.name} executed successfully!`);
        console.log('Bot execution result:', response.data.result);
        
        // Update bot status to success
        updateBotStatus(botKey, 'success');
      } else {
        message.destroy(); // Remove loading message
        message.error(response.data.message || `Error running ${config.name}`);
        
        // Update bot status to failed
        updateBotStatus(botKey, 'failed');
      }
      
    } catch (error) {
      console.error('API Error:', error);
      message.destroy(); // Remove loading message
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.statusText;
        message.error(`Error running ${config.name}: ${errorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        message.error(`Error running ${config.name}: No response from server`);
      } else {
        // Something else happened
        message.error(`Error running ${config.name}: ${error.message}`);
      }
      
      // Update bot status to failed
      updateBotStatus(botKey, 'failed');
    }
  };

  // Update bot status after execution
  const updateBotStatus = (botKey, status) => {
    // Only update local state for immediate feedback
    setData(prevData => 
      prevData.map(item => 
        item.key === botKey 
          ? { 
              ...item, 
              status: status === 'success' ? 'Active' : 'Failed',
              lastRun: 'Just now'
            }
          : item
      )
    );
    
    // Refresh bot executions data after bot execution to get real data from API
    setTimeout(() => {
      fetchBotExecutions();
    }, 3000); // Wait 3 seconds for the bot to complete and data to be saved
  };

  const handleRunBot = (record) => {
    // Set loading state for this specific button
    setLoadingStates(prev => ({ ...prev, [record.key]: true }));
    
    // Get bot configuration
    const config = botConfig[record.key];
    
    if (!config) {
      message.error(`No configuration found for bot: ${record.name}`);
      setLoadingStates(prev => ({ ...prev, [record.key]: false }));
      return;
    }
    
    // Execute bot dynamically
    executeBot(record.key, config)
      .finally(() => {
        // Clear loading state for this specific button
        setLoadingStates(prev => ({ ...prev, [record.key]: false }));
      });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Success Rate',
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate) => (
        rate !== null ? (
          <Space>
            <Progress 
              percent={rate} 
              size="small" 
              strokeColor={rate >= 90 ? '#52c41a' : rate >= 70 ? '#faad14' : '#ff4d4f'}
              showInfo={false}
            />
            <span>{rate}%</span>
          </Space>
        ) : (
          <span>-</span>
        )
      ),
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRun',
      key: 'lastRun',
      render: (text, record) => {
        const executionData = getBotExecutionData(record.name);
        if (executionData) {
          const executionTime = new Date(executionData.executionTime).toLocaleString();
          return (
            <div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {executionTime}
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                {executionData.description}
              </div>
            </div>
          );
        }
        return text;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleRunBot(record)}
            loading={loadingStates[record.key] || false}
            style={{
              width: 101,
              height: 37,
              borderRadius: 50,
              backgroundColor: '#68D241E8',
              border: 'none',
              opacity: 1
            }}
            className="custom-run-button"
          >
            Run
          </Button>
        </Space>
      ),
    },
  ];

  const totalBots = data.length;
  const activeBots = data.filter(item => item.status === 'Active').length;
  const totalRuns = data.reduce((sum, item) => sum + item.totalRuns, 0);
  const totalSuccessfulRuns = data.reduce((sum, item) => sum + item.successfulRuns, 0);
  const overallSuccessRate = totalRuns > 0 ? Math.round((totalSuccessfulRuns / totalRuns) * 100) : 0;

  return (
    <div className={`admin-page ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div className="admin-header-banner">
        <div className="hero-content">
          <Title level={1} className="hero-title-news">
            Government & NSDL
          </Title>
          <Paragraph className="hero-description">
            Manage government securities data and NSDL (National Securities Depository Limited) operations.
          </Paragraph>
        </div>
      </div>

      {/* Main Content */}
      <Card className="admin-main-card">
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Government & NSDL
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={handleRunAll}
              loading={runAllLoading}
            >
              Run All Bots
            </Button>
          </Col>
        </Row>

       

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          loading={runAllLoading || loadingExecutions}
          className="admin-table"
        />
      </Card>
    </div>
  );
};

export default GovernmentNSDL;
